import React, { useEffect, useMemo, useState } from 'react';
import s from './AuditLogsTab.module.sass';

const sourceLabels = {
    admin_panel: 'Админка',
    support_chat: 'Чат',
    site_interaction: 'Сайт',
    frontend_deterrent: 'Защита',
    deploy_check: 'Проверка',
    unknown: 'Другое'
};

// Метаданные источников: подпись, тон (цвет через data-tone в SASS) и иконка
const SOURCE_META = {
    admin_panel: {
        label: 'Админка',
        tone: 'admin',
        icon: (<><path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"></path><path d="M12 2v2"></path><path d="M12 20v2"></path><path d="m4.9 4.9 1.4 1.4"></path><path d="m17.7 17.7 1.4 1.4"></path><circle cx="12" cy="12" r="3"></circle></>)
    },
    support_chat: {
        label: 'Чат',
        tone: 'chat',
        icon: (<><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></>)
    },
    site_interaction: {
        label: 'Сайт',
        tone: 'site',
        icon: (<><path d="m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z"></path><path d="m13 13 6 6"></path></>)
    },
    frontend_deterrent: {
        label: 'Защита',
        tone: 'guard',
        icon: (<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></>)
    },
    deploy_check: {
        label: 'Система',
        tone: 'system',
        icon: (<><rect x="2" y="2" width="20" height="8" rx="2"></rect><rect x="2" y="14" width="20" height="8" rx="2"></rect><line x1="6" y1="6" x2="6.01" y2="6"></line><line x1="6" y1="18" x2="6.01" y2="18"></line></>)
    },
    config: {
        label: 'Конфиг',
        tone: 'system',
        icon: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></>)
    },
    unknown: {
        label: 'Другое',
        tone: 'other',
        icon: (<><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></>)
    }
};

const getSourceMeta = (source) => SOURCE_META[source] || SOURCE_META.unknown;

// Тон события: ошибки/блокировки красные, защита — янтарь, иначе нейтрально
const getSeverity = (log) => {
    const a = log.action || '';
    if (/error|blocked|fail|duplicate/i.test(a)) return 'bad';
    if (log.source === 'frontend_deterrent') return 'warn';
    if (/success|save_user_success|delivered/i.test(a)) return 'good';
    return 'neutral';
};

const modalLabels = {
    commission: 'Комиссия',
    verification: 'Верификация',
    success: 'Успех',
    tax: 'Налог',
    cryptoConversion: 'Конвертация крипты',
    bankConversion: 'Банковская конвертация',
    code: 'Код',
    premium: 'Премиум доступ'
};

const actionLabels = {
    dom_mutation_suspected: 'Изменение DOM',
    contextmenu_blocked: 'Правый клик заблокирован',
    shortcut_blocked: 'Горячая клавиша заблокирована',
    selection_blocked: 'Выделение заблокировано',
    drag_blocked: 'Перетаскивание заблокировано',
    field_blur: 'Заполнено поле',
    field_change: 'Изменено поле',
    button_click: 'Нажата кнопка',
    menu_click: 'Переход по меню',
    dropdown_toggle: 'Открыт/закрыт блок',
    file_select: 'Выбран файл',
    tariff_click: 'Выбран тариф',
    dropdown_item_click: 'Выбран пункт',
    bank_select: 'Выбран банк',
    crypto_select: 'Выбрана криптовалюта',
    modal_open: 'Открыт модал',
    open: 'Открыто',
    close: 'Закрыто',
    close_click: 'Закрыто кнопкой',
    close_unmount: 'Чат закрыт',
    message_send: 'Отправлено сообщение',
    message_input_blur: 'Введено сообщение',
    message_enter_key: 'Enter в поле сообщения',
    message_edit_open: 'Редактирование сообщения',
    message_edit_save: 'Сообщение изменено',
    message_delete: 'Сообщение удалено',
    message_restore: 'Сообщение восстановлено',
    message_drag_start: 'Перетаскивание сообщения',
    message_drag_drop: 'Сообщение перемещено',
    send_button_click_edit_mode: 'Кнопка отправки',
    send_button_click_message: 'Кнопка отправки',
    send_button_click_presets: 'Открыты пресеты',
    author_select: 'Выбран автор',
    author_select_cancel: 'Выбор автора отменён',
    preset_select: 'Выбран пресет',
    clear_chat: 'Чат очищен',
    agent_name_edit_open: 'Редактирование агента',
    agent_name_edit_save: 'Имя агента изменено',
    agent_number_edit_open: 'Редактирование номера агента',
    agent_number_edit_save: 'Номер агента изменён',
    save_user_attempt: 'Сохранение юзера',
    save_user_success: 'Юзер сохранён',
    save_user_error: 'Ошибка сохранения',
    save_user_validation_error: 'Ошибка валидации',
    save_user_no_changes: 'Без изменений',
    save_user_duplicate_dialog: 'Найден дубликат',
    send_action_email_attempt: 'Отправка письма',
    send_action_email_blocked: 'Письмо заблокировано',
    send_action_email_success: 'Письмо отправлено',
    send_action_email_error: 'Ошибка письма',
    send_registration_email_attempt: 'Отправка регистрации',
    send_registration_email_success: 'Регистрация отправлена',
    send_registration_email_error: 'Ошибка регистрации',
    send_success_email_attempt: 'Отправка успеха',
    send_success_email_success: 'Успех отправлен',
    send_success_email_error: 'Ошибка успеха',
    config_save: 'Сохранён конфиг'
};

const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// Дата (МСК) в формате dd.mm.yyyy для группировки по дням
const moscowDateKey = (offsetDays = 0) => {
    const d = new Date(Date.now() + offsetDays * 86400000);
    return new Intl.DateTimeFormat('ru-RU', {
        timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric'
    }).format(d);
};

// Ярлык дня для лога на основе atDisplay ("28.08.2026, 23:10:09")
const getDayLabel = (log) => {
    const key = String(log.atDisplay || '').split(',')[0].trim();
    if (!key) return 'Без даты';
    if (key === moscowDateKey(0)) return 'Сегодня';
    if (key === moscowDateKey(-1)) return 'Вчера';
    return key;
};

// Время события (МСК) HH:MM:SS
const getLogTime = (log) => {
    const parts = String(log.atDisplay || '').split(',');
    if (parts[1]) return parts[1].trim();
    return log.atMoscow || log.at || '—';
};

const stringifyDetails = (details) => {
    try {
        return JSON.stringify(details || {}, null, 2);
    } catch (_) {
        return '{}';
    }
};

const formatValue = (value, fallback = '—') => {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'boolean') return value ? 'да' : 'нет';
    if (Array.isArray(value)) return value.filter(Boolean).join(', ') || fallback;
    if (typeof value === 'object') {
        return value.text || value.name || value.email || value.id || fallback;
    }
    const text = String(value).replace(/\s+/g, ' ').trim();
    return text.length > 160 ? `${text.slice(0, 160)}...` : text || fallback;
};

const formatMessage = (message) => {
    if (!message) return '';
    if (typeof message === 'string') return formatValue(message);
    const author = message.author ? `${message.author}: ` : '';
    return `${author}${formatValue(message.text || message.originalText || message.id)}`;
};

const formatUserContext = (details) => {
    const chunks = [];
    if (details.name || details.userName) chunks.push(`Юзер: ${details.name || details.userName}`);
    if (details.email) chunks.push(`Email: ${details.email}`);
    if (details.bankAccount) chunks.push(`Счёт: ${details.bankAccount}`);
    if (details.configFile) chunks.push(`Конфиг: ${details.configFile}`);
    if (details.checkpoint !== undefined && details.checkpoint !== null) chunks.push(`Checkpoint: ${details.checkpoint}`);
    return chunks.join(' · ');
};

const formatChatContext = (details) => {
    const chunks = [];
    if (details.userName) chunks.push(`Клиент: ${details.userName}`);
    if (details.messagesCount !== undefined) chunks.push(`Сообщений: ${details.messagesCount}`);
    if (details.agentName) chunks.push(`Агент: ${details.agentName}${details.agentNumber ? ` #${details.agentNumber}` : ''}`);
    return chunks.join(' · ');
};

const getPrimaryDetail = (log) => {
    const details = log.details || {};

    if (log.action === 'config_save') {
        if (details.isNew) return `Создан конфиг: ${details.configFile || '—'}`;
        const n = details.changedCount || 0;
        const first = Array.isArray(details.changes) && details.changes[0]
            ? ` · ${details.changes[0].path}: ${formatValue(details.changes[0].from)} → ${formatValue(details.changes[0].to)}`
            : '';
        return `${details.configFile || 'конфиг'} · изменено полей: ${n}${first}`;
    }
    if (log.action === 'modal_open' || details.modal) {
        return `Модал: ${modalLabels[details.modal] || details.template?.name || details.modal || 'неизвестно'}`;
    }
    if ((log.action === 'button_click' || log.action === 'dropdown_toggle') && details.buttonText) {
        return `Кнопка: ${details.buttonText}`;
    }
    if (log.action === 'menu_click' && details.buttonText) {
        return `Пункт меню: ${details.buttonText}`;
    }
    if (details.files?.length) {
        return `Файлы: ${details.files.join(', ')}`;
    }
    if (details.actionKey) {
        return `Действие: ${details.actionKey}`;
    }
    if (details.fieldLabel) {
        return `${details.fieldLabel}: ${details.value ?? ''}`;
    }
    if (log.action === 'message_send') {
        return `Автор: ${formatValue(details.author)} · Текст: ${formatMessage(details.message) || formatValue(details.pendingMessage)}`;
    }
    if (log.action === 'message_input_blur' || log.action === 'send_button_click_message' || log.action === 'message_enter_key') {
        return `Текст: ${formatValue(details.typedMessage || details.value || details.pendingMessage)}`;
    }
    if (log.action === 'send_button_click_presets') {
        return `Открыт список пресетов: ${details.availablePresets?.length || 0}`;
    }
    if (log.action === 'author_select') {
        return `Автор: ${formatValue(details.author)} · Сообщение: ${formatValue(details.pendingMessage)}`;
    }
    if (log.action === 'author_select_cancel') {
        return `Отменено · Сообщение: ${formatValue(details.pendingMessage)}`;
    }
    if (log.action === 'preset_select') {
        return `Пресет: ${formatValue(details.presetName)} · Сообщений: ${details.renderedMessages?.length || details.originalMessages?.length || 0}`;
    }
    if (log.action === 'message_edit_open') {
        return `Открыто сообщение: ${formatMessage(details.message)}`;
    }
    if (log.action === 'message_edit_save') {
        return `Было: ${formatMessage(details.before)} · Стало: ${formatValue(details.afterText)}`;
    }
    if (log.action === 'message_delete') {
        return `Удалено: ${formatMessage(details.message)} · Позиция: ${details.index ?? '—'}`;
    }
    if (log.action === 'message_restore') {
        return `Восстановлено: ${formatMessage(details.message)}`;
    }
    if (log.action === 'message_drag_start') {
        return `Перетаскивается: ${formatMessage(details.message)}`;
    }
    if (log.action === 'message_drag_drop') {
        return `Перемещено: ${formatMessage(details.message)} · ${details.fromIndex ?? '—'} → ${details.toIndex ?? '—'}`;
    }
    if (log.action === 'clear_chat') {
        return `Очищено сообщений: ${details.messages?.length || 0}`;
    }
    if (log.action === 'agent_name_edit_open') {
        return `Текущее имя: ${formatValue(details.agentName)}`;
    }
    if (log.action === 'agent_name_edit_save') {
        return `Имя: ${formatValue(details.from)} → ${formatValue(details.to)}`;
    }
    if (log.action === 'agent_number_edit_open') {
        return `Текущий номер: ${formatValue(details.agentNumber)}`;
    }
    if (log.action === 'agent_number_edit_save') {
        return `Номер: ${formatValue(details.from)} → ${formatValue(details.to)}`;
    }
    if (log.action?.startsWith('send_')) {
        const parts = [];
        if (details.actionKey) parts.push(`Действие: ${details.actionKey}`);
        if (details.template?.name) parts.push(`Шаблон: ${details.template.name}`);
        if (details.email || details.userEmail) parts.push(`Email: ${details.email || details.userEmail}`);
        if (details.error) parts.push(`Ошибка: ${details.error}`);
        return parts.join(' · ') || formatUserContext(details) || log.label;
    }
    if (log.action?.startsWith('save_user')) {
        const parts = [formatUserContext(details)].filter(Boolean);
        if (details.error) parts.push(`Ошибка: ${details.error}`);
        if (details.reason) parts.push(`Причина: ${details.reason}`);
        return parts.join(' · ') || log.label;
    }
    if (details.itemText) {
        return `Выбор: ${details.itemText}`;
    }
    if (details.tariffText) {
        return `Тариф: ${details.tariffText}`;
    }
    if (log.action === 'bank_select' && details.bank) {
        return `Банк: ${details.bank}`;
    }
    if (log.action === 'crypto_select') {
        return `Криптовалюта: ${formatValue(details.crypto || details.coin || details.name || details.itemText)}`;
    }
    if (details.typedMessage) {
        return `Сообщение: ${details.typedMessage}`;
    }
    if (details.presetName) {
        return `Пресет: ${details.presetName}`;
    }
    if (details.attribute) {
        return `DOM изменён: атрибут ${details.attribute}`;
    }
    if (details.key || details.code) {
        return `Клавиша: ${details.key || details.code}`;
    }
    if (details.target?.text) {
        return details.target.text;
    }

    return formatUserContext(details) || formatChatContext(details) || details.section || details.path || log.label || log.action || 'Событие';
};

const getLocationDetail = (log) => {
    const details = log.details || {};

    if (details.modal) {
        return `modal:${modalLabels[details.modal] || details.modal}`;
    }
    if (details.buttonText) {
        return `button:${details.buttonText}`;
    }
    if (details.fieldLabel) {
        return `field:${details.fieldLabel}`;
    }
    if (log.source === 'support_chat') {
        return formatChatContext(details) || 'Чат поддержки';
    }
    if (log.source === 'admin_panel') {
        return formatUserContext(details) || 'Админка';
    }

    return details.section || details.configFile || details.path || log.ip || 'Без объекта';
};

const AuditLogsTab = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [source, setSource] = useState('');
    const [account, setAccount] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const [showAccountDropdown, setShowAccountDropdown] = useState(false);
    const [showPageSizeDropdown, setShowPageSizeDropdown] = useState(false);
    const [expanded, setExpanded] = useState(new Set());
    const [lastUpdated, setLastUpdated] = useState(null);
    const [streamConnected, setStreamConnected] = useState(false);

    const loadLogs = async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);
            setError('');

            const response = await fetch('/api/audit/logs?days=7&limit=800', {
                headers: getAuthHeaders(),
                cache: 'no-store'
            });
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Не удалось загрузить логи');
            }

            setLogs(Array.isArray(data.logs) ? data.logs : []);
            setLastUpdated(new Date());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLogs();

        const token = localStorage.getItem('authToken');
        if (!token) return undefined;

        const stream = new EventSource(`/api/audit/stream?token=${encodeURIComponent(token)}`);

        stream.addEventListener('connected', () => {
            setStreamConnected(true);
        });

        stream.addEventListener('audit', (event) => {
            try {
                const record = JSON.parse(event.data);
                setLogs(prev => {
                    if (prev.some(item => item.id === record.id)) return prev;
                    return [record, ...prev].slice(0, 800);
                });
                setLastUpdated(new Date());
            } catch (_) {
                // Ignore malformed stream events.
            }
        });

        stream.onerror = () => {
            setStreamConnected(false);
        };

        return () => {
            stream.close();
            setStreamConnected(false);
        };
    }, []);

    const accounts = useMemo(() => {
        return Array.from(new Set(logs.map(log => log.actor?.username).filter(Boolean))).sort((a, b) => a.localeCompare(b));
    }, [logs]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest(`.${s.dropdownWrapper}`)) {
                setShowAccountDropdown(false);
                setShowPageSizeDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, []);

    const filteredLogs = useMemo(() => {
        const query = search.trim().toLowerCase();

        return logs.filter(log => {
            if (source && log.source !== source) return false;
            if (account && log.actor?.username !== account) return false;
            if (!query) return true;

            const haystack = [
                log.atDisplay,
                log.atMoscow,
                log.actor?.username,
                log.actor?.role,
                log.source,
                log.action,
                log.label,
                stringifyDetails(log.details)
            ].filter(Boolean).join(' ').toLowerCase();

            return haystack.includes(query);
        });
    }, [logs, search, source, account]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, source, account, itemsPerPage]);

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / itemsPerPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const pageStartIndex = (safeCurrentPage - 1) * itemsPerPage;
    const paginatedLogs = filteredLogs.slice(pageStartIndex, pageStartIndex + itemsPerPage);
    const pageEndIndex = Math.min(pageStartIndex + itemsPerPage, filteredLogs.length);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    // Группировка текущей страницы по дням (Сегодня/Вчера/дата)
    const groupedByDay = useMemo(() => {
        const groups = [];
        let current = null;
        paginatedLogs.forEach((log) => {
            const label = getDayLabel(log);
            if (!current || current.label !== label) {
                current = { label, items: [] };
                groups.push(current);
            }
            current.items.push(log);
        });
        return groups;
    }, [paginatedLogs]);

    const toggleExpanded = (id) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const hasFilters = search.trim() || source || account;
    const clearFilters = () => { setSearch(''); setSource(''); setAccount(''); };

    return (
        <div className={s.auditLogsTab} data-audit-ignore="true">
            <section className={s.panel}>
                <header className={s.panelHead}>
                    <div className={s.panelHeadMain}>
                        <span className={s.panelIcon}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 11l3 3L22 4" />
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                            </svg>
                        </span>
                        <div className={s.panelHeadText}>
                            <h2 className={s.panelTitle}>Журнал действий</h2>
                            <p className={s.panelSub}>Скрытый аудит действий сотрудников за последние 7 дней</p>
                        </div>
                    </div>
                    <div className={s.panelHeadSide}>
                        <span className={`${s.liveStatus} ${streamConnected ? s.connected : s.disconnected}`}>
                            <span className={s.liveDot}></span>
                            {streamConnected ? 'Live' : 'Подключение'}
                        </span>
                        <span className={s.updatedChip}>
                            Обновлено {lastUpdated ? lastUpdated.toLocaleTimeString('ru-RU') : '—'}
                        </span>
                        <button className={s.refreshButton} onClick={() => loadLogs()} title="Обновить">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="23 4 23 10 17 10"></polyline>
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                            </svg>
                            <span>Обновить</span>
                        </button>
                    </div>
                </header>

                {/* Поиск + фильтры */}
                <div className={s.controls}>
                    <div className={s.searchContainer}>
                        <span className={s.searchIcon}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                        </span>
                        <input
                            className={s.searchInput}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Поиск по логину, действию, полю, email, счёту…"
                        />
                        {search && (
                            <button className={s.searchClear} onClick={() => setSearch('')} title="Очистить">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        )}
                    </div>

                    <div className={s.dropdownWrapper}>
                        <button
                            type="button"
                            className={`${s.dropdownButton} ${account ? s.dropdownActive : ''}`}
                            onClick={() => {
                                setShowAccountDropdown(prev => !prev);
                                setShowPageSizeDropdown(false);
                            }}
                        >
                            <svg className={s.ddIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                            <span>{account || 'Все аккаунты'}</span>
                            <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points={showAccountDropdown ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}></polyline>
                            </svg>
                        </button>
                        {showAccountDropdown && (
                            <div className={s.dropdownMenu}>
                                <div className={s.dropdownHeader}>
                                    <span>Аккаунт сотрудника</span>
                                    {account && (
                                        <button type="button" className={s.clearAllButton} onClick={() => setAccount('')}>Очистить</button>
                                    )}
                                </div>
                                <div className={s.checkboxList}>
                                    <button
                                        type="button"
                                        className={`${s.dropdownOptionButton} ${account === '' ? s.active : ''}`}
                                        onClick={() => { setAccount(''); setShowAccountDropdown(false); }}
                                    >
                                        <span>Все аккаунты</span>
                                        {account === '' && <span className={s.dropdownOptionCheck}>✓</span>}
                                    </button>
                                    {accounts.map(item => (
                                        <button
                                            key={item}
                                            type="button"
                                            className={`${s.dropdownOptionButton} ${account === item ? s.active : ''}`}
                                            onClick={() => { setAccount(item); setShowAccountDropdown(false); }}
                                        >
                                            <span>{item}</span>
                                            {account === item && <span className={s.dropdownOptionCheck}>✓</span>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={s.dropdownWrapper}>
                        <button
                            type="button"
                            className={s.dropdownButton}
                            onClick={() => {
                                setShowPageSizeDropdown(prev => !prev);
                                setShowAccountDropdown(false);
                            }}
                        >
                            <span>{itemsPerPage} / стр.</span>
                            <svg className={s.ddChevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points={showPageSizeDropdown ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}></polyline>
                            </svg>
                        </button>
                        {showPageSizeDropdown && (
                            <div className={`${s.dropdownMenu} ${s.dropdownMenuNarrow}`}>
                                <div className={s.checkboxList}>
                                    {[25, 50, 100, 200].map(size => (
                                        <button
                                            key={size}
                                            type="button"
                                            className={`${s.dropdownOptionButton} ${itemsPerPage === size ? s.active : ''}`}
                                            onClick={() => { setItemsPerPage(size); setShowPageSizeDropdown(false); }}
                                        >
                                            <span>{size} записей</span>
                                            {itemsPerPage === size && <span className={s.dropdownOptionCheck}>✓</span>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {hasFilters && (
                        <button className={s.resetFilters} onClick={clearFilters}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                            <span>Сбросить</span>
                        </button>
                    )}
                    {hasFilters && (
                        <span className={s.foundNote}>Найдено: <strong>{filteredLogs.length}</strong></span>
                    )}
                </div>
            </section>

            {error && <div className={s.error}>{error}</div>}
            {loading && (
                <div className={s.loading}>
                    <span className={s.spinner} />
                    <p>Загрузка логов…</p>
                </div>
            )}

            {!loading && (
                filteredLogs.length === 0 ? (
                    <div className={s.emptyState}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={s.emptyIcon}>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                        </svg>
                        <p>{hasFilters ? 'По заданным фильтрам логи не найдены' : 'Логи отсутствуют'}</p>
                        {hasFilters && (
                            <button className={s.clearButton} onClick={clearFilters}>Сбросить фильтры</button>
                        )}
                    </div>
                ) : (
                    <div className={s.logsStream}>
                        {groupedByDay.map((group) => (
                            <div key={group.label} className={s.dayGroup}>
                                <div className={s.dayHeader}>
                                    <span className={s.dayLabel}>{group.label}</span>
                                    <span className={s.dayLine} />
                                    <span className={s.dayCount}>{group.items.length}</span>
                                </div>
                                {group.items.map((log) => {
                                    const isOpen = expanded.has(log.id);
                                    const meta = getSourceMeta(log.source);
                                    const actor = log.actor?.username || 'Неизвестно';
                                    const role = log.actor?.role || '—';
                                    const action = actionLabels[log.action] || log.action;
                                    const primaryDetail = getPrimaryDetail(log);
                                    const severity = getSeverity(log);

                                    return (
                                        <article key={log.id} className={`${s.logLine} ${isOpen ? s.logLineOpen : ''}`} data-severity={severity}>
                                            <button className={s.logLineButton} onClick={() => toggleExpanded(log.id)}>
                                                <span className={s.srcIcon} data-tone={meta.tone}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{meta.icon}</svg>
                                                </span>
                                                <span className={s.logMain}>
                                                    <span className={s.logTopRow}>
                                                        <span className={s.logAction}>{action}</span>
                                                        <span className={s.srcPill} data-tone={meta.tone}>{meta.label}</span>
                                                    </span>
                                                    <span className={s.logDetail}>{primaryDetail}</span>
                                                    <span className={s.logMeta}>
                                                        <span className={s.metaChip} title="Сотрудник">
                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                                <circle cx="12" cy="7" r="4"></circle>
                                                            </svg>
                                                            <span>{actor}</span>
                                                            <span className={s.metaRole}>{role}</span>
                                                        </span>
                                                        <span className={s.metaChip} title="Объект">
                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M9 18V5l12-2v13"></path>
                                                                <circle cx="6" cy="18" r="3"></circle>
                                                                <circle cx="18" cy="16" r="3"></circle>
                                                            </svg>
                                                            <span>{getLocationDetail(log)}</span>
                                                        </span>
                                                    </span>
                                                </span>
                                                <span className={s.rightCol}>
                                                    <time className={s.logTime}>{getLogTime(log)}</time>
                                                    <span className={`${s.chevron} ${isOpen ? s.open : ''}`}>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <polyline points="6 9 12 15 18 9"></polyline>
                                                        </svg>
                                                    </span>
                                                </span>
                                            </button>

                                            {isOpen && (
                                                <div className={s.details}>
                                                    <div className={s.detailsGrid}>
                                                        <div><span>UTC</span><strong>{log.at}</strong></div>
                                                        <div><span>МСК</span><strong>{log.atMoscow || log.atDisplay}</strong></div>
                                                        <div><span>IP</span><strong>{log.ip || '—'}</strong></div>
                                                        <div><span>User Agent</span><strong>{log.userAgent || '—'}</strong></div>
                                                    </div>
                                                    {log.action === 'config_save' && Array.isArray(log.details?.changes) && log.details.changes.length > 0 && (
                                                        <div className={s.configDiff}>
                                                            <div className={s.configDiffHead}>
                                                                Изменённые поля: {log.details.changedCount}
                                                                {log.details.truncated ? ` (показаны первые ${log.details.changes.length})` : ''}
                                                            </div>
                                                            {log.details.changes.map((c, i) => (
                                                                <div key={`${c.path}-${i}`} className={s.configDiffRow}>
                                                                    <span className={s.configDiffPath}>{c.path}</span>
                                                                    <span className={s.configDiffFrom}>{formatValue(c.from)}</span>
                                                                    <span className={s.configDiffArrow}>→</span>
                                                                    <span className={s.configDiffTo}>{formatValue(c.to)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <pre className={s.detailsJson}>{stringifyDetails(log.details)}</pre>
                                                </div>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )
            )}

            {!loading && filteredLogs.length > 0 && (
                <div className={s.paginationBar}>
                    <div className={s.paginationInfo}>
                        Страница <strong>{safeCurrentPage}</strong> из <strong>{totalPages}</strong>
                        <span>Показано {pageStartIndex + 1}–{pageEndIndex} из {filteredLogs.length}</span>
                    </div>
                    <div className={s.paginationActions}>
                        <button type="button" className={s.pageButton} onClick={() => setCurrentPage(1)} disabled={safeCurrentPage === 1}>В начало</button>
                        <button type="button" className={s.pageButton} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={safeCurrentPage === 1}>Назад</button>
                        <button type="button" className={s.pageButton} onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={safeCurrentPage === totalPages}>Вперёд</button>
                        <button type="button" className={s.pageButton} onClick={() => setCurrentPage(totalPages)} disabled={safeCurrentPage === totalPages}>В конец</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuditLogsTab;
